/**
 * @GestionalePD Admin charge details UI
 */
import dayjs from 'dayjs';
import 'dayjs/locale/it';

import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { Container, Box, Card, Grid, CardContent, CardHeader, IconButton } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
//---------------------------------------------------------------------------------------
import {
  PopAnimation,
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_DELAY,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from '../../utils/popUpStyles';
import { DefaultOutlinedButton, DefaultContainedButton } from '../../utils/Buttons';
import { CustomSnackbar, CreateSnackbarFunctions } from '../../utils/Snackbar';
import { CustomTextField, CustomDatePicker } from '../../utils/StyledInputs';
import { HttpErrorHandler, ErrorUI } from '../../utils/ErrorHandler';
import { EmptyPageUI, LoadingUI } from '../../utils/LayoutStyles';
import { PositionStatusLabel } from '../../utils/PositionStatusLabel';
import API from '../../api';

dayjs.locale('it');

/**
 * @returns Admin position details UI
 */
export default function PositionDetail() {
  // general
  const { state } = useLocation();
  const { chargeID } = state;
  const { canModify } = state;
  const [charge, setCharge] = useState([]);
  const [isModify, setModify] = useState(false);
  const [hasCharge, setHasCharge] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentProfile, setCurrentProfile] = useState('');

  // charge details variables declaration
  const [chargeUserID, setChargeUserID] = useState('');
  const [chargePositionID, setChargePositionID] = useState('');
  const [chargeStartDate, setChargeStartDate] = useState('');
  const [chargeEndDate, setChargeEndDate] = useState('');
  const [chargeInstitutionalEmail, setChargeInstitutionalEmail] = useState('');
  const [chargeUserName, setChargeUserName] = useState('');

  // popup handler
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [onPopUpError, setPopUpError] = useState(false);

  // snackbar handler
  const {
    open: openEditPositionDataSnack,
    handleOpen: handleOpenEditPositionDataSnack,
    handleClose: handleCloseEditPositionDataSnack,
  } = CreateSnackbarFunctions(false);

  // change start date handler
  const handleStartDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY/MM/DD');
    setChargeStartDate(formattedDate);
  };

  // change end date handler
  const handleEndDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY/MM/DD');
    setChargeEndDate(formattedDate);
  };

  // update charge details request
  const onUpdateCharge = (e) => {
    e.preventDefault();

    const chargeData = {
      user_id: chargeUserID,
      position_id: chargePositionID,
      start_date: dayjs(chargeStartDate).format('YYYY/MM/DD'),
      end_date:
        dayjs(chargeEndDate).format('YYYY/MM/DD') !== 'Invalid Date' ? dayjs(chargeEndDate).format('YYYY/MM/DD') : null,
      email: chargeInstitutionalEmail,
    };

    let itemsLeft;

    Object.keys(chargeData).forEach((key) => {
      if (key === 'start_date') {
        const value = chargeData[key];

        if (Object.prototype.hasOwnProperty.call(chargeData, key)) {
          if (value === undefined || value === null || value === '' || value === 'Invalid Date') {
            itemsLeft = true;

            handleOpenEditPositionDataSnack();

            setTimeout(() => {
              handleCloseEditPositionDataSnack();
            }, POPUP_DURATION_DELAY);
          }
        }
      }
    });

    if (!itemsLeft) {
      if (dayjs(chargeData.end_date).isBefore(dayjs(chargeData.start_date))) {
        setPopUpError(true);
        setPopUpText('La data di fine non può essere antecedente a quella di inizio');
        setOpen(true);
        setTimeout(() => {
          setOpen(false);
        }, POPUP_DURATION_ERROR);
      } else {
        API.put(`/charges/${chargeID}`, chargeData)
          .then((response) => {
            setModify(false);
            setPopUpError(false);
            setPopUpText('Informazioni salvate');
            setOpen(true);
            setTimeout(() => {
              setOpen(false);
            }, POPUP_DURATION_SUCCESS);
          })
          .catch((error) => {
            HttpErrorHandler(error, setPopUpText);
            setPopUpError(true);
            setOpen(true);
            setTimeout(() => {
              setOpen(false);
            }, POPUP_DURATION_ERROR);
          });
      }
    }
  };

  // get charge api details by charge id
  const getPositionDetail = () => {
    setErrorMessage(null);

    API.get(`/charges/${chargeID}`)
      .then((response) => {
        setCharge(response.data);
        setChargeUserID(response.data.user_id);
        setChargePositionID(response.data.position_id);
        setChargeStartDate(response.data.start_date);
        setChargeEndDate(response.data.end_date);
        setChargeInstitutionalEmail(response.data.email);
        setChargeUserName(`${response.data.user_name} ${response.data.user_surname}`);
      })
      .catch((error) => HttpErrorHandler(error, setErrorMessage))
      .finally(() => setHasCharge(true));
  };

  // initial charge data loading
  useEffect(() => {
    getPositionDetail();
  }, []);

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  // if charge id not found redirect to 404 page
  if (!chargeID) return <Navigate to={{ pathname: '/404' }} />;

  return hasCharge ? (
    <Container maxWidth="xl">
      <Grid container height="100%">
        {charge.length !== 0 && !errorMessage ? (
          <Grid item xs={12}>
            {/* charge details card */}
            <Card sx={{ minWidth: 275 }}>
              <CardHeader
                title={chargeUserName}
                subheader={charge.position_name}
                action={<IconButton disabled>{PositionStatusLabel(charge, 'icon')}</IconButton>}
              />

              <CardContent>
                <form onSubmit={onUpdateCharge}>
                  <Grid container spacing={3}>
                    {/* charge institutional e-mail */}
                    <Grid item xs={12}>
                      <CustomTextField
                        disabled={!isModify}
                        name="charge-email"
                        label="E-mail Istituzionale"
                        type="email"
                        defaultValue={chargeInstitutionalEmail}
                        onChange={(e) => setChargeInstitutionalEmail(e.currentTarget.value)}
                      />
                    </Grid>

                    {/* charge start date */}
                    <Grid item xs={12} md={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <CustomDatePicker
                          disabled={!isModify}
                          label="Data Inizio *"
                          value={dayjs(chargeStartDate)}
                          onChange={handleStartDateChange}
                          maxDate={dayjs(chargeEndDate)}
                        />
                      </LocalizationProvider>
                    </Grid>

                    {/* charge end date */}
                    <Grid item xs={12} md={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <CustomDatePicker
                          disabled={!isModify}
                          label="Data Fine"
                          value={dayjs(chargeEndDate)}
                          onChange={handleEndDateChange}
                          minDate={dayjs(chargeStartDate)}
                        />
                      </LocalizationProvider>
                    </Grid>

                    {/* charge card action buttons */}
                    <Grid item xs={12} sx={{ display: canModify ? 'block' : 'none' }}>
                      <Box display="flex" justifyContent="flex-end" sx={{ '& button': { m: '0 10px' } }}>
                        <DefaultOutlinedButton
                          id="charge-close-button"
                          variant="outlined"
                          onClick={() => setModify(false)}
                          customStyles={{ display: isModify ? 'block' : 'none' }}
                        >
                          Annulla
                        </DefaultOutlinedButton>

                        <DefaultContainedButton
                          id="charge-modify-button"
                          variant="contained"
                          customStyles={{ display: isModify ? 'none' : 'block' }}
                          onClick={() => setModify(true)}
                        >
                          Modifica
                        </DefaultContainedButton>

                        <DefaultContainedButton
                          id="charge-save-button"
                          variant="contained"
                          type="submit"
                          customStyles={{ display: isModify ? 'block' : 'none' }}
                        >
                          Salva
                        </DefaultContainedButton>
                      </Box>
                    </Grid>

                    {/* popup element UI */}
                    <Grid item xs={12}>
                      <Container ref={setAnchor}>
                        <BasePopup anchor={anchor} open={open}  >
                              {onPopUpError ? (
                                <PopupErrorBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupErrorBody>
                              ) : (
                                <PopupSuccessBody>{popUpText}</PopupSuccessBody>
                              )}
                        </BasePopup>
                      </Container>
                    </Grid>
                  </Grid>
                </form>

                {/* snackbar UI */}
                <CustomSnackbar
                  open={openEditPositionDataSnack}
                  handleClose={handleCloseEditPositionDataSnack}
                  message="Inserisci i dati mancanti!"
                />
              </CardContent>
            </Card>
          </Grid>
        ) : errorMessage ? (
          // error handler UI
          ErrorUI(errorMessage)
        ) : (
          // empty page ui
          EmptyPageUI('Nessuna carica trovata')
        )}
      </Grid>
    </Container>
  ) : (
    // loading spinner UI
    LoadingUI()
  );
}
